import React, { useState, useEffect } from 'react';
import { X, PackagePlus, AlertCircle } from 'lucide-react';
import { episService } from '../../../services/epis.service';
import SearchableSelect from '../../../components/SearchableSelect';
import type { EpiEstoqueSaldoContract } from '@sstexames/shared-types';

interface EntradaEstoqueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  empresaId: number;
}

export const EntradaEstoqueModal: React.FC<EntradaEstoqueModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  empresaId,
}) => {
  const [loading, setLoading] = useState(false);
  const [saldos, setSaldos] = useState<EpiEstoqueSaldoContract[]>([]);
  const [selectedEpiId, setSelectedEpiId] = useState<number | ''>('');
  const [quantidade, setQuantidade] = useState<number | ''>(10);
  const [notaFiscal, setNotaFiscal] = useState('');
  const [lote, setLote] = useState('');
  const [valorUnitario, setValorUnitario] = useState<number | ''>('');
  const [motivo, setMotivo] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !empresaId) return;
    const fetchSaldos = async () => {
      try {
        const data = await episService.getEstoqueSaldos(empresaId);
        setSaldos(data || []);
      } catch (err) {
        console.error('Erro ao carregar estoque:', err);
      }
    };
    fetchSaldos();
  }, [isOpen, empresaId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!selectedEpiId) {
      setErrorMessage('Selecione o EPI para dar entrada.');
      return;
    }
    if (!quantidade || Number(quantidade) <= 0) {
      setErrorMessage('Informe uma quantidade válida maior que zero.');
      return;
    }

    try {
      setLoading(true);
      await episService.registrarEntradaCompra({
        empresa_id: empresaId,
        epi_id: Number(selectedEpiId),
        quantidade: Number(quantidade),
        nota_fiscal: notaFiscal || undefined,
        lote: lote || undefined,
        valor_unitario: valorUnitario ? Number(valorUnitario) : undefined,
        motivo: motivo || undefined,
      });

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error('Erro ao registrar entrada de compra:', err);
      const errorMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao registrar entrada no estoque.';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
              <PackagePlus className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Entrada de Compra / Reposição</h3>
              <p className="text-xs text-gray-500">Alimentação de estoque e registro no Kardex</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errorMessage && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-2.5 rounded-xl text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <SearchableSelect
              label="Equipamento (EPI)"
              required
              options={saldos.map((s) => ({
                id: s.epi_id,
                label: `${s.epi_descricao} (Saldo atual: ${s.saldo_disponivel} un.)`,
              }))}
              value={selectedEpiId || null}
              onChange={(val) => setSelectedEpiId(val ? Number(val) : '')}
              placeholder="Selecione um EPI do catálogo..."
              searchPlaceholder="Buscar EPI por descrição..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Quantidade Adquirida *
              </label>
              <input
                type="number"
                min="1"
                required
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Nota Fiscal (NF-e)
              </label>
              <input
                type="text"
                placeholder="Ex: NF-99238"
                value={notaFiscal}
                onChange={(e) => setNotaFiscal(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 text-gray-800"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Lote de Fabricação
              </label>
              <input
                type="text"
                placeholder="Ex: LOT-2026-X"
                value={lote}
                onChange={(e) => setLote(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Valor Unitário (R$)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={valorUnitario}
                onChange={(e) => setValorUnitario(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 text-gray-800"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              Observações / Fornecedor
            </label>
            <input
              type="text"
              placeholder="Ex: Fornecedor ABC Distribuidora Ltda."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 text-gray-800"
            />
          </div>

          <div className="border-t border-gray-100 pt-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-2xs"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-all shadow-sm disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Registrar Entrada de Estoque'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
